import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/api";
import SeoHead from "@/lib/SeoHead";
import { ArrowRight } from "lucide-react";

export function Industries() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/industries").then((r) => setItems(r.data || [])); }, []);
  return (
    <>
      <SeoHead title="Industries Served | Durga Auto Industries" description="Industries we supply: automotive, construction, oil & gas, railways, aerospace and general manufacturing." />
      <PageHero overline="Industries" title="Industries we serve" subtitle="From automotive chassis to refinery flanges — our fasteners hold what matters." />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((i) => (
            <Link key={i.id} to={`/industries/${i.slug}`} className="card-light p-8 group block" data-testid={`industry-card-${i.slug}`}>
              <div className="w-14 h-14 bg-[#1C1C1C] text-[#C1121F] flex items-center justify-center font-heading font-bold text-2xl mb-5 group-hover:bg-[#C1121F] group-hover:text-white transition-colors">{i.name?.charAt(0)}</div>
              <h3 className="h-card text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{i.name}</h3>
              <p className="mt-3 font-body text-sm text-[#4B5563]">{i.description}</p>
              <div className="mt-5 inline-flex items-center gap-2 font-heading font-semibold text-sm text-[#C1121F]">
                Explore industry <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

export function CaseStudies() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/case-studies").then((r) => setItems(r.data || [])); }, []);
  return (
    <>
      <SeoHead title="Case Studies | Durga Auto Industries" description="Real-world fastener engineering projects across automotive, oil & gas and infrastructure." />
      <PageHero overline="Case Studies" title="Engineering in the field" subtitle="Selected client projects — challenges, solutions, results." />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-8">
          {items.map((c) => (
            <Link key={c.id} to={`/case-studies/${c.slug}`} className="group card-light overflow-hidden" data-testid={`case-card-${c.slug}`}>
              <div className="aspect-[16/10] bg-[#0A0A0A] overflow-hidden">
                {c.cover_image && <img src={c.cover_image} alt={c.title} className="w-full h-full object-cover opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500" loading="lazy" />}
              </div>
              <div className="p-8">
                <div className="overline mb-2">{c.industry}</div>
                <h3 className="h-card text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors">{c.title}</h3>
                <p className="mt-3 font-body text-sm text-[#4B5563] line-clamp-2">{c.summary}</p>
                <div className="mt-5 inline-flex items-center gap-2 font-heading font-semibold text-sm text-[#C1121F]">
                  Read case <ArrowRight size={14} className="group-hover:translate-x-1 transition-transform" />
                </div>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

export function CaseStudyDetail() {
  const slug = window.location.pathname.split("/").pop();
  const [cs, setCs] = useState(null);
  useEffect(() => { api.get(`/case-studies/${slug}`).then((r) => setCs(r.data)).catch(() => setCs({ notFound: true })); }, [slug]);
  if (!cs) return null;
  if (cs.notFound) return <PageHero overline="404" title="Case study not found" />;
  return (
    <>
      <SeoHead title={cs.seo_title || cs.title} description={cs.seo_description || cs.summary} image={cs.cover_image} />
      <PageHero overline={cs.industry} title={cs.title} subtitle={cs.summary} />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 lg:grid-cols-[1fr_2fr] gap-12">
          <aside className="space-y-6">
            <Block label="Client" value={cs.client} />
            <Block label="Industry" value={cs.industry} />
            {cs.results?.length > 0 && (
              <div>
                <div className="overline mb-3">Results</div>
                <ul className="space-y-2 font-body text-sm text-[#1C1C1C]">
                  {cs.results.map((r, i) => <li key={i} className="flex gap-2"><span className="text-[#C1121F]">●</span> {r}</li>)}
                </ul>
              </div>
            )}
          </aside>
          <div className="prose max-w-none font-body text-[#1C1C1C] whitespace-pre-line leading-relaxed">{cs.content}</div>
        </div>
      </section>
    </>
  );
}

export function Blog() {
  const [posts, setPosts] = useState([]);
  useEffect(() => { api.get("/blog").then((r) => setPosts(r.data || [])); }, []);
  return (
    <>
      <SeoHead title="Knowledge Center | Durga Auto Industries" description="Engineering insights, technical guides and industry best practices." />
      <PageHero overline="Knowledge Center" title="Engineering insights & guides" subtitle="Practical articles on fastener selection, materials, finishes and quality." />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {posts.map((p) => (
            <Link key={p.id} to={`/blog/${p.slug}`} className="group card-light overflow-hidden" data-testid={`blog-card-${p.slug}`}>
              <div className="aspect-[16/10] bg-[#F5F5F5] overflow-hidden">
                {p.cover_image && <img src={p.cover_image} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" loading="lazy" />}
              </div>
              <div className="p-6">
                <div className="overline mb-2">{p.tags?.[0] || "Article"}</div>
                <h3 className="font-heading font-semibold text-lg text-[#1C1C1C] group-hover:text-[#C1121F] transition-colors line-clamp-2">{p.title}</h3>
                <p className="mt-3 font-body text-sm text-[#4B5563] line-clamp-3">{p.excerpt}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>
    </>
  );
}

export function BlogPost() {
  const slug = window.location.pathname.split("/").pop();
  const [post, setPost] = useState(null);
  useEffect(() => { api.get(`/blog/${slug}`).then((r) => setPost(r.data)).catch(() => setPost({ notFound: true })); }, [slug]);
  if (!post) return null;
  if (post.notFound) return <PageHero overline="404" title="Post not found" />;
  return (
    <>
      <SeoHead title={post.seo_title || post.title} description={post.seo_description || post.excerpt} keywords={post.seo_keywords} image={post.cover_image} />
      <article>
        <PageHero overline={post.tags?.[0] || "Article"} title={post.title} subtitle={post.excerpt} image={post.cover_image} />
        <section className="section-y bg-white">
          <div className="container-x max-w-3xl">
            <div className="prose max-w-none font-body text-[#1C1C1C] whitespace-pre-line leading-relaxed text-lg">{post.content}</div>
            <div className="mt-12 pt-8 border-t border-[#E5E7EB] flex items-center justify-between">
              <Link to="/blog" className="font-heading font-semibold text-[#C1121F]">← All articles</Link>
              <Link to="/contact" className="btn-primary">Talk to engineering</Link>
            </div>
          </div>
        </section>
      </article>
    </>
  );
}

export function Testimonials() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/testimonials").then((r) => setItems(r.data || [])); }, []);
  return (
    <>
      <SeoHead title="Client Testimonials | Durga Auto Industries" description="What our clients say about working with Durga Auto Industries." />
      <PageHero overline="Testimonials" title="What our clients say" />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 md:grid-cols-2 gap-6">
          {items.map((t) => (
            <div key={t.id} className="card-light p-8" data-testid={`testimonial-card-${t.id}`}>
              <p className="font-body text-[#1C1C1C] leading-relaxed text-lg">"{t.content}"</p>
              <div className="mt-6 pt-5 border-t border-[#E5E7EB]">
                <div className="font-heading font-semibold text-[#1C1C1C]">{t.name}</div>
                <div className="font-body text-sm text-[#4B5563] mt-1">{t.role}{t.company && ` · ${t.company}`}</div>
              </div>
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function FaqPage() {
  const [items, setItems] = useState([]);
  const [openId, setOpenId] = useState(null);
  useEffect(() => { api.get("/faqs").then((r) => setItems(r.data || [])); }, []);
  const faqJsonLd = {
    "@context": "https://schema.org", "@type": "FAQPage",
    mainEntity: items.map((f) => ({ "@type": "Question", name: f.question, acceptedAnswer: { "@type": "Answer", text: f.answer } })),
  };
  return (
    <>
      <SeoHead title="Frequently Asked Questions | Durga Auto Industries" description="Answers to common questions about our fasteners, certifications, ordering and shipping." jsonLd={faqJsonLd} />
      <PageHero overline="FAQ" title="Frequently asked questions" />
      <section className="section-y bg-white">
        <div className="container-x max-w-3xl space-y-3">
          {items.map((f) => (
            <div key={f.id} className="border border-[#E5E7EB] hover:border-[#C1121F] transition-colors" data-testid={`faq-item-${f.id}`}>
              <button onClick={() => setOpenId(openId === f.id ? null : f.id)} className="w-full text-left p-5 flex items-center justify-between gap-6">
                <span className="font-heading font-semibold text-[#1C1C1C]">{f.question}</span>
                <span className="text-[#C1121F] font-heading font-bold text-2xl">{openId === f.id ? "−" : "+"}</span>
              </button>
              {openId === f.id && <div className="px-5 pb-5 font-body text-[#4B5563] leading-relaxed">{f.answer}</div>}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function CertificationsPage() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/certifications").then((r) => setItems(r.data || [])); }, []);
  return (
    <>
      <SeoHead title="Certifications & Quality | Durga Auto Industries" description="ISO 9001, IATF 16949 and other quality system certifications." />
      <PageHero overline="Certifications" title="Quality you can audit." />
      <section className="section-y bg-white">
        <div className="container-x grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {items.map((c) => (
            <div key={c.id} className="card-light p-8" data-testid={`cert-${c.id}`}>
              <h3 className="h-card text-[#1C1C1C]">{c.name}</h3>
              <p className="mt-3 font-body text-sm text-[#4B5563]">{c.description}</p>
              {c.issued_by && <div className="font-body text-xs text-[#A3A3A3] mt-4 uppercase tracking-wider">Issued by {c.issued_by}</div>}
            </div>
          ))}
        </div>
      </section>
    </>
  );
}

export function StaticPageView() {
  const slug = window.location.pathname.replace(/^\//, "");
  const [page, setPage] = useState(null);
  useEffect(() => { api.get(`/pages/${slug}`).then((r) => setPage(r.data)).catch(() => setPage({ notFound: true })); }, [slug]);
  if (!page) return null;
  if (page.notFound) return <PageHero overline="404" title="Page not found" />;
  return (
    <>
      <SeoHead title={page.seo_title || page.title} description={page.seo_description} keywords={page.seo_keywords} />
      <PageHero overline="" title={page.title} />
      <section className="section-y bg-white">
        <div className="container-x max-w-3xl prose font-body text-[#1C1C1C] whitespace-pre-line leading-relaxed">{page.content}</div>
      </section>
    </>
  );
}

export function NotFound() {
  return (
    <div className="min-h-[70vh] flex flex-col items-center justify-center text-center px-6">
      <div className="overline mb-4">404</div>
      <h1 className="h-section text-[#1C1C1C]">Page not found</h1>
      <p className="mt-4 font-body text-[#4B5563]">The page you're looking for doesn't exist.</p>
      <Link to="/" className="btn-primary mt-8">Back to Home</Link>
    </div>
  );
}

function PageHero({ overline, title, subtitle, image }) {
  return (
    <section className="bg-[#1C1C1C] text-white pt-20 pb-16 border-b border-[#333] relative overflow-hidden">
      {image && (<><div className="absolute inset-0 opacity-25"><img src={image} alt="" className="w-full h-full object-cover" /><div className="absolute inset-0 bg-gradient-to-r from-[#1C1C1C] to-transparent" /></div></>)}
      <div className="relative container-x">
        {overline && <div className="overline mb-4">{overline}</div>}
        <h1 className="font-heading font-bold text-4xl md:text-5xl max-w-3xl">{title}</h1>
        {subtitle && <p className="mt-4 body-lg text-white/70 font-body max-w-2xl">{subtitle}</p>}
      </div>
    </section>
  );
}

function Block({ label, value }) {
  if (!value) return null;
  return (
    <div>
      <div className="overline mb-2">{label}</div>
      <div className="font-heading font-semibold text-[#1C1C1C]">{value}</div>
    </div>
  );
}
